import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

import { useDimensions } from "./useDimensions";

const Motivation = ({ data }) => {
    const svgRef = useRef();

    const containerRef = useRef();
    const dimensions = useDimensions(containerRef);
    
    useEffect(() => {

        if (!data || data.length === 0) return;

        const motivationCounts = d3.rollup(
            data,
            v => v.length,
            d => d.motivation
        );

        const motivations = Array.from(motivationCounts, ([key, value]) => ({ 
            motivation: key, 
            count: value
        })).sort((a, b) => b.count - a.count);
        
        // const width = 400;
        // const height = 400;
        const { width, height } = dimensions;
        const margin = { top: 20, right: 0, bottom: 50, left: 18 };
        

        const xScale = d3.scaleBand()
            .domain(motivations.map(d => d.motivation))
            .range([0, width - margin.left - margin.right])
            .padding(0.65);

        const yScale = d3.scaleLinear()
            .domain([0, d3.max(motivations, d => d.count)])
            .nice()
            .range([height - margin.top - margin.bottom, 0]);

        d3.select(svgRef.current).selectAll('*').remove();
            
        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        svg.append("g")
            .attr("class", "svg-grid")
            .call(
                d3.axisLeft(yScale)
                .ticks(5)
                .tickSize(-width + margin.left + margin.right)
                .tickFormat("")
            )
            .select(".domain")
            .attr("stroke", "none");

        const Tooltip = d3.select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("pointer-events", "none")
            .style("opacity", 0);

        const handleMouseOver = (event , d) => {
            Tooltip.style("opacity", 1);
            d3.select(event.currentTarget)
                .attr("stroke", "#fff");
        };

        const handleMouseMove = (event, d) => {
            Tooltip
                .html(`动机：<b>${d.motivation}</b>（<span class="tooltip__num">${d.count}</span c>）
                    `)
                .style("left", (event.pageX + 10) + "px")
                .style("top",(event.pageY - 10) + "px");
        };

        const handleMouseOut = (event) => {
            Tooltip.style("opacity", 0);
            d3.select(event.currentTarget)
                .attr("stroke", "var(--china");
        };
            
            
        svg.selectAll(".bar")
            .data(motivations)
            .enter()
            .append("rect")
            .attr("class", "bar")
            .attr("x", d => xScale(d.motivation))
            .attr("width", xScale.bandwidth())
            .attr("y", d => yScale(d.count))
            .attr("height", d => Math.max(0, height - margin.top - margin.bottom - yScale(d.count)))
            .attr("fill", "var(--china)")
            .attr("fill-opacity", 0.8)
            .attr("stroke", "var(--china)")
            .attr("stroke-opacity", 1)
            .on("mouseover", handleMouseOver)
            .on("mousemove", handleMouseMove)
            .on("mouseout", handleMouseOut);

        // Add axes
        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(-3, ${height - margin.top - margin.bottom})`)
            .call(d3.axisBottom(xScale)
                .tickPadding(10)
                .tickSize(0))
            .selectAll("text")
            .attr("transform", "rotate(-45)")
            .style("text-anchor", "end");

        svg.append("g")
            .attr("class","y-axis")  
            .call(d3.axisLeft(yScale)
                .ticks(5)
                .tickPadding(5)
                .tickSize(0)
                .tickFormat(d => d !== 0 ? d : ""))
            .select(".domain")
            .attr("opacity", 0);

    }, [data, dimensions]);


  return (
    <div className="center-container" >
        <div className="article-body">
            <p className="subheader">“他的敌人不是个人，而是整体的社会” </p>
            <h2> 犯罪者特有“受害者情结”</h2>
            <div className="spacerdiv" />
            <p>从新闻报道和官方通报的内容，我们能进一步描摹嫌疑人的犯罪动机。歪脑整理发现，无差别犯罪动机包括家庭冲突、财务困境（例如失业、创业或投资失败）、精神障碍（精神疾病史、癫痫和抑郁症）、工作压力与纠纷（包括与上司或同事的冲突 、被降职和过度劳累等）、对政府的不满（例如土地使用纠纷等）以及毒品引起的幻觉。</p>

            <h4>中国袭击者的犯罪动机</h4>
            <div ref={containerRef} style={{ height:"300px" }}>
                <svg ref={svgRef}></svg>
             </div>
             <div className="spacerdiv" />
    
             <p>歪脑对比了犯罪动机与年龄性别、职业分类、省份之间的关系，发现因对政府不满而施暴的案件数和嫌疑人年龄成正相关。歪脑发现，对政府不满而触发施暴的案件多发生在40岁以上的嫌疑人身上，而且在2010年后，该类动因的案件不断增加。</p>
            <p>“失意”的中年男性群体也是中国各类社会问题的聚焦点。有学者提出，由于中国社会体制的变化和社会不平等的存在，“失意群体”很少或没有机会通过通过合法的途径去实现目标，“社会结构限制了他们获得成功的机会”。中年男性有更强的社会经验，也更有可能直接经历分配不公、官员腐败、司法不公等社会问题。台湾大学法律学教授李茂生在分析杀人动机时谈到, “他的敌人不是个人, 是整体的社会...但无法摧毁整个国家，就随机挑人下手。”</p>

            <p>李厚辰也将无差别袭击事件比作一种“特殊形式的自杀”, 他用日本“电车卧轨自杀”的现象与之类比。日本卧轨自杀者的家人通常要付给铁路公司巨额赔款，同时也会造成高峰期交通堵塞。“确实有人自杀的时候会愿意给其他人带来麻烦和痛苦，来达到自杀时的疏解。” 他也认为，怨气是这种情绪的根源，这是一种特殊的受害者情结。当一个人认为-无法满足于自己的生存状态及能力，并长期处于一种“受害”的状态，他就会在这个过程产生巨大的怨气。</p>

    
        </div>

    </div>
    // <div className="grid-container-right">
    //     <div>
    //         <h2>怨气，一种特殊的受害者情结</h2>
    //         <p>从新闻报道和官方通报的内容，我们能进一步描摹嫌疑人的犯罪动机。歪脑整理发现，无差别犯罪动机包括家庭冲突、财务困境（例如失业、创业或投资失败）、精神障碍（精神疾病史、癫痫和抑郁症）、工作压力与纠纷（包括与上司或同事的冲突 、被降职和过度劳累等）、对政府的不满（例如土地使用纠纷等）以及毒品引起的幻觉。</p>
    //         <p>图
    //             歪脑对比了犯罪动机与年龄性别、职业分类、省份之间的关系，发现因对政府不满而施暴的案件数和嫌疑人年龄成正相关。歪脑发现，对政府不满而触发施暴的案件多发生在40岁以上的嫌疑人身上，而且在2010年后，该类动因的案件不断增加。
    //         </p>
    //         <p>“失意”的中年男性群体也是中国各类社会问题的聚焦点。有学者提出，由于中国社会体制的变化和社会不平等的存在，“失意群体”很少或没有机会通过通过合法的途径去实现目标，“社会结构限制了他们获得成功的机会”. 中年男性有更强的社会经验，也更有可能直接经历分配不公、官员腐败、司法不公等社会问题。台湾大学法律学教授李茂生在分析其杀人动机时谈到, “他的敌人不是个人, 是整体的社会...但无法摧毁整个国家，就随机挑人下手。”</p>

    //         <p>李厚辰也将无差别袭击事件比作一种“特殊形式的自杀”, 他用日本“电车卧轨自杀”的现象与之类比。日本卧轨自杀者的家人通常要付给铁路公司巨额赔款，同时也会造成高峰期交通堵塞。“确实有人自杀的时候会愿意给其他人带来麻烦和痛苦，来达到自杀时的疏解。” 他也认为，怨气是这种情绪的根源，这是一种特殊的受害者情结。当一个人认为自己无法满足自己生存状态的能力，并长期处于一种“受害”的状态，他就会在这个过程产生巨大的怨气。</p>

    //     </div>

    //     <div ref={containerRef} style={{ height:"500px" }}>
    //         <svg ref={svgRef}></svg>
    //     </div>
        
    // </div>
  )
}

export default Motivation;